import React, { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useElementWidth } from "../../../utils/useElementWidth";

export default function Table({
  children,
  className,
  dark,
  noOverflow,
  fixedHeader,
}) {
  const tableRef = useRef(null);
  const parentRef = useRef(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  // We can't use sticky header (via CSS) if the table is wider than the parent.
  // That's because we need to omit "overflow-x-visible" for sticky header to work.
  const tableWidth = useElementWidth(tableRef);
  const parentWidth = useElementWidth(parentRef);
  const shouldStickHeaders = fixedHeader && tableWidth <= parentWidth;

  // Add a white background to the header when the header is sticky.
  // It captures the scroll event and checks if the header is sticky.
  useEffect(() => {
    if (!shouldStickHeaders || !tableRef.current) return;

    const table = tableRef.current;
    const thead = table.querySelector("thead");

    if (!thead) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeaderSticky(!entry.isIntersecting);
      },
      {
        threshold: 1.0,
        rootMargin: "0px 0px -1px 0px",
      }
    );

    observer.observe(thead);

    return () => {
      observer.disconnect();
    };
  }, [shouldStickHeaders]);

  return (
    <div
      ref={parentRef}
      className={twMerge(
        "mx-[-1.5rem] w-[calc(100%+3rem)]",
        !noOverflow && "overflow-x-auto",
        shouldStickHeaders && "overflow-x-visible",
        className
      )}
    >
      <table
        ref={tableRef}
        className={twMerge(
          "w-full table-auto rounded",
          "[&_th]:py-3 [&_td]:py-3 [&_th]:px-2 [&_th]:border-b [&_td]:px-2",
          "[&_tr>:first-child]:pl-6 [&_tr>:last-child]:pr-6",
          "[&_tbody_tr:last-of-type>td]:border-b-0",
          "[&_td]:border-black/5 [&_th]:border-black/5",
          "[&_tbody_tr:hover]:text-black [&_tr]:transition-colors duration-200",
          dark
            ? "[&_tbody_tr:hover]:bg-gray-500/50"
            : "[&_tbody_tr:hover]:bg-white",
          shouldStickHeaders && "[&_th]:sticky [&_th]:top-0 [&_th]:z-10",
          shouldStickHeaders && isHeaderSticky && "[&_th]:bg-white",
          className
        )}
      >
        {children}
      </table>
    </div>
  );
}
